(() => {
  "use strict";
  const physicalSlot = (slot, assetIndex) =>
      assetIndex === 1 ? slot : 100 + (slot - 1) * 3 + assetIndex - 1,
    assetsOf = (file) =>
      file
        ? [
            file.url ? { ...file, attachments: void 0, assetIndex: 1 } : null,
            ...(file.attachments || []),
          ].filter(Boolean)
        : [],
    integer = (value, min, max) => {
      const number = Number(value);
      if (!Number.isSafeInteger(number) || number < min || number > max)
        throw Error(
          "\u6570\u91CF\u5FC5\u987B\u662F\u8303\u56F4\u5185\u7684\u6B63\u6574\u6570\u3002",
        );
      return number;
    },
    countFromAdditional = (n) => 1 + integer(n, 0, 4),
    create = (input = {}) => {
      const count = integer(input.count ?? input.quantities?.length ?? 1, 1, 5),
        quantities = Array.from({ length: count }, (_, i) =>
          integer(input.quantities?.[i] ?? input.quantity ?? 1, 1, 1e6),
        ),
        sides = integer(input.sides ?? 1, 1, 2),
        files = {};
      for (let v = 1; v <= count; v++)
        for (const side of sides === 2 ? ["front", "back"] : ["front"]) {
          const key = `${v}:${side}`;
          if (input.files?.[key]) {
            const file = input.files[key],
              attachments = (file.attachments || []).map((asset) => ({
                ...asset,
              }));
            if (
              attachments.length > 3 ||
              attachments.some(
                (asset) =>
                  !Number.isInteger(asset.assetIndex) ||
                  asset.assetIndex < 2 ||
                  asset.assetIndex > 4,
              ) ||
              new Set(attachments.map((asset) => asset.assetIndex)).size !==
                attachments.length
            )
              throw Error(
                "\u6BCF\u4E2A\u4E0A\u4F20\u4F4D\u7F6E\u6700\u591A\u4E0A\u4F204\u4E2A\u6587\u4EF6\u3002",
              );
            files[key] = { ...file, attachments };
          }
        }
      const sameFrontBack = Array.from(
        { length: count },
        (_, i) =>
          sides === 2 &&
          input.sameFrontBack?.[i] === !0 &&
          !assetsOf(files[`${i + 1}:back`]).length,
      );
      return {
        count,
        quantities,
        quantity: quantities[0],
        sides,
        files,
        sameFrontBack,
      };
    },
    plan = (input) => {
      const state = create(input);
      return state.quantities.flatMap((quantity, i) =>
        (state.sides === 2 ? ["front", "back"] : ["front"]).map((side) => {
          const sameAsFront = side === "back" && state.sameFrontBack[i];
          return {
            key: `${i + 1}:${side}`,
            version: i + 1,
            quantity,
            side,
            slot: i * 2 + (side === "back" ? 2 : 1),
            sameAsFront,
            label: `\u7248\u672C${i + 1}${state.sides === 2 ? (side === "front" ? "\u6B63\u9762" : "\u80CC\u9762") : "\u753B\u9762"}${sameAsFront ? "\uFF08\u4E0E\u6B63\u9762\u76F8\u540C\uFF09" : ""}`,
            file:
              state.files[`${i + 1}:${sameAsFront ? "front" : side}`] || null,
          };
        }),
      );
    },
    resize = (input, count, sides = input.sides) => {
      const state = create(input);
      return (
        integer(count, 1, 5),
        create({
          ...state,
          count,
          sides,
          quantities: Array.from(
            { length: count },
            (_, i) => state.quantities[i] ?? 1,
          ),
        })
      );
    },
    remove = (input, version) => {
      const state = create(input);
      if ((integer(version, 1, state.count), state.count === 1))
        throw Error("\u81F3\u5C11\u4FDD\u7559\u4E00\u4E2A\u7248\u672C\u3002");
      const files = {};
      for (const row of plan(state))
        row.version === version ||
          !row.file ||
          row.sameAsFront ||
          (files[
            `${row.version > version ? row.version - 1 : row.version}:${row.side}`
          ] = row.file);
      return create({
        ...state,
        count: state.count - 1,
        quantities: state.quantities.filter((_, i) => i !== version - 1),
        sameFrontBack: state.sameFrontBack.filter((_, i) => i !== version - 1),
        files,
      });
    },
    setFile = (input, version, side, file, assetIndex = 1) => {
      const state = create(input);
      if (
        (integer(version, 1, state.count),
        !["front", "back"].includes(side) ||
          (state.sides === 1 && side === "back"))
      )
        throw Error("\u5370\u5237\u9762\u65E0\u6548\u3002");
      if (!file || !/^https:\/\//i.test(String(file.url || "")))
        throw Error("\u6587\u4EF6\u5730\u5740\u65E0\u6548\u3002");
      integer(assetIndex, 1, 4);
      const key = `${version}:${side}`,
        previous = state.files[key] || {},
        stored =
          assetIndex === 1
            ? { ...file, assetIndex, attachments: previous.attachments || [] }
            : {
                ...previous,
                attachments: [
                  ...(previous.attachments || []).filter(
                    (asset) => asset.assetIndex !== assetIndex,
                  ),
                  { ...file, assetIndex },
                ].sort((a, b) => a.assetIndex - b.assetIndex),
              };
      return create({ ...state, files: { ...state.files, [key]: stored } });
    },
    clearAsset = (input, version, side, assetIndex) => {
      const state = create(input),
        key = `${version}:${side}`,
        file = state.files[key];
      if (!file) return state;
      const attachments = (file.attachments || []).filter(
          (asset) => asset.assetIndex !== assetIndex,
        ),
        stored = assetIndex === 1 ? { attachments } : { ...file, attachments },
        files = { ...state.files };
      return (
        stored.url || attachments.length
          ? (files[key] = stored)
          : delete files[key],
        create({ ...state, files })
      );
    },
    serialize = (input) => {
      const state = create(input),
        rows = plan(state);
      return {
        versionCount: state.count,
        versionQuantities: [...state.quantities],
        printSides: state.sides,
        sameFrontBack: [...state.sameFrontBack],
        totalPrintQuantity: state.quantities.reduce((sum, q) => sum + q, 0),
        artworkVersions: state.quantities.map((quantity, i) => ({
          version: i + 1,
          quantity,
          sameFrontBack: state.sameFrontBack[i],
          slots: rows
            .filter((row) => row.version === i + 1)
            .map(({ slot, side, label, file, sameAsFront }) => ({
              slot,
              side,
              label,
              sameAsFront,
              fileId: (file?.url && file.id) || "",
              fileIds: assetsOf(file).map((asset) => asset.id || ""),
            })),
        })),
        uploadedFiles: rows.flatMap((row) =>
          assetsOf(row.file).map((asset) => ({
            ...asset,
            slot: physicalSlot(row.slot, asset.assetIndex),
            uploadPosition: row.slot,
            version: row.version,
            side: row.side,
            sameAsFront: row.sameAsFront,
            role: asset.assetIndex === 1 ? "primary" : "reference",
            label: `${row.label}${asset.assetIndex}`,
          })),
        ),
      };
    },
    validateFile = (file) => {
      if (
        !file ||
        !/\.(pdf|ai|eps|jpg|jpeg|png|zip|rar)$/i.test(file.name || "") ||
        !Number.isFinite(file.size) ||
        file.size < 1 ||
        file.size > 314572800
      )
        throw Error(
          "\u8BF7\u9009\u62E9\u5141\u8BB8\u683C\u5F0F\u3001\u5927\u5C0F\u4E0D\u8D85\u8FC7300\u5146\u5B57\u8282\u7684\u975E\u7A7A\u6587\u4EF6\u3002",
        );
      return file;
    },
    mount = (host, options) => {
      if (
        !host ||
        typeof options?.upload != "function" ||
        typeof options?.onChange != "function"
      )
        throw Error(
          "\u5FC5\u987B\u63A5\u5165\u4E0A\u4F20\u548C\u540E\u53F0\u6838\u4EF7\u624D\u80FD\u542F\u7528\u3002",
        );
      if (host._easyMultiVersionUpload) return host._easyMultiVersionUpload;
      let state = create({
          ...options,
          count: options.paperArtwork ? (options.count ?? options.quantities?.length ?? 1) : 1,
          quantity: 1,
          quantities: options.paperArtwork ? Array(options.count ?? options.quantities?.length ?? 1).fill(1) : [1],
        }),
        busy = !1,
        destroyed = !1,
        pending = !1,
        revision = 0;
      const doc = host.ownerDocument,
        paperArtwork = options.paperArtwork === true,
        isPrintSet = !paperArtwork && /上方印刷数量/.test(options.quantityDescription || ""),
        quantityLabel = isPrintSet ? "套数" : "数量",
        make = (tag, text) => {
          const node = doc.createElement(tag);
          return (text && (node.textContent = text), node);
        },
        section = make("section");
      ((section.className = "easy-multi-version-upload easy-upload--compact"),
        section.setAttribute("data-easy-multi-version-upload", ""));
      if (options.filesOnly) section.classList.add('easy-upload--files-only');
      const style = make("style");
      ((style.textContent =
        ".easy-multi-version-upload{margin:20px 0}.easy-multi-version-upload h3{font-size:18px;margin:0 0 12px}.easy-multi-version-upload fieldset{border:1px solid #b8dce6;border-radius:8px;padding:14px;margin:12px 0;min-width:0}.easy-multi-version-upload button{padding:9px 14px;margin:4px;border:1px solid #26a9d1;border-radius:6px;background:#fff;color:#126980;cursor:pointer}.easy-multi-version-upload button[aria-pressed=true]{background:#e7f6fc;font-weight:700}.easy-multi-version-upload button:disabled{opacity:.5;cursor:wait}.easy-multi-version-upload input[type=number]{width:90px;margin:0 12px;padding:8px;border:1px solid #26a9d1;border-radius:5px}.easy-multi-version-upload [data-easy-upload-slot]{display:flex;align-items:center;gap:8px;flex-wrap:wrap;margin-top:8px}.easy-multi-version-upload span{overflow-wrap:anywhere}.easy-multi-version-upload p{line-height:1.5;margin:8px 0}"),
        (style.textContent +=
          ".easy-multi-version-upload .easy-upload-four-instructions{font-size:13px;line-height:1.6;margin:10px 0;color:#46535d}.easy-upload-four-files{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:8px;width:100%;min-width:0}.easy-upload-four-file{display:flex;flex-direction:column;gap:5px;padding:8px;border:1px solid #d5e6ed;border-radius:6px;min-width:0;background:#fff;font-size:12px}.easy-upload-four-file img{width:100%;height:72px;object-fit:contain;background:#f1f5f6}.easy-upload-four-file a{overflow-wrap:anywhere;line-height:1.4}.easy-upload-four-file button{font-size:12px!important;padding:6px!important;min-height:32px!important;margin:0!important}.easy-upload-four-file strong{font-size:12px!important;line-height:1.4}.easy-upload-four-files:empty{display:none}"));
      const title = make("h3", "\u4E0A\u4F20\u753B\u9762"),
        toolbar = make("div"),
        add = make("button", "\uFF0B\u589E\u52A0\u4E0D\u540C\u753B\u9762");
      style.textContent += '.easy-upload--files-only{margin:0}.easy-upload--files-only>h3,.easy-upload--files-only>.easy-upload-guidance,.easy-upload--files-only>.easy-upload-toolbar,.easy-upload--files-only>.easy-upload-total,.easy-upload--files-only>.easy-upload-help,.easy-upload--files-only>.easy-upload-limits,.easy-upload--files-only .easy-upload-card-header,.easy-upload--files-only .easy-upload-set-explanation,.easy-upload--files-only legend{display:none!important}.easy-upload--files-only fieldset{margin:0;padding:8px}.easy-upload--files-only [data-easy-upload-slot]>strong{display:none}.easy-upload--files-only>p.easy-upload-four-instructions{font-size:12px}';
      (add.setAttribute("data-easy-add-version", ""),
        (style.textContent +=
          ".easy-multi-version-upload [data-easy-add-version]{width:auto!important;min-width:0!important;min-height:40px!important;padding:9px 16px!important;margin:0!important;font-size:14px!important;line-height:1.4!important;background:#26a9d1;color:#fff}.easy-multi-version-upload [data-easy-delete-version]{width:auto!important;min-height:32px!important;padding:6px 10px!important;font-size:13px!important;background:#fff!important;color:#126980!important}"),
        (style.textContent +=
          ".easy-multi-version-upload .easy-same-front-back{display:flex!important;align-items:flex-start;gap:8px;width:100%;margin:12px 0 6px;line-height:1.5;font-size:14px;float:none}.easy-multi-version-upload .easy-same-front-back input[type=checkbox]{width:18px;height:18px;min-width:18px;margin:2px 0 0;accent-color:#26a9d1}.easy-multi-version-upload .easy-same-front-back-hint{font-size:13px;line-height:1.5;color:#b43b24;margin:6px 0 10px;width:100%;clear:both}"),
        (style.textContent +=
          ".easy-multi-version-upload fieldset>label.easy-same-front-back{grid-column:1 / -1!important;grid-row:2!important;white-space:normal!important;width:auto!important;min-width:0!important}.easy-multi-version-upload fieldset>.easy-same-front-back-hint{grid-column:1 / -1!important;grid-row:3!important}.easy-multi-version-upload fieldset>.easy-same-front-back span{min-width:0!important;white-space:normal!important}.easy-multi-version-upload [data-easy-upload-slot]>button{font-size:13px!important;padding:8px!important;line-height:1.4!important;height:auto!important;min-height:40px!important;white-space:normal!important}"),
        (add.type = "button"));
      const summary = make("p"),
        rows = make("div"),
        status = make("p"),
        instructions = make(
          "p",
          options.filesOnly ? "最多4个文件，单个不超过300 MB" : "每面最多4个文件，单个不超过300 MB",
        );
      const guidance = make(
          "p",
          paperArtwork ? '相同图案增加上方印刷数量，不同图案点击“增加不同画面”，最多5种图案。' : `相同画面增加${isPrintSet ? '套数' : '数量'}，不同画面点击“增加不同画面”，最多5种画面。`,
        ),
        help = make("details"),
        helpTitle = make("summary", "上传说明"),
        helpText = make(
          "p",
          "请按正面、背面和画面版本上传对应文件。所有上传文件均用于制作；如需排版，请在备注中说明。制作数量以所选数量为准，不按文件个数自动增加。",
        ),
        limits = make(
          "p",
          "单个文件不超过300 MB · 支持PDF、AI、EPS、JPG、JPEG、PNG、ZIP、RAR",
        ),
        largeHelp = make("details"),
        largeTitle = make("summary", "文件太大怎么办？"),
        largeText = make("p", "超大文件请发送至 "),
        email = make("a", "jiemg1989@gmail.com");
      guidance.className = "easy-upload-guidance";
      toolbar.className = "easy-upload-toolbar";
      rows.className = "easy-upload-artworks";
      summary.className = "easy-upload-total";
      status.className = "easy-upload-operation-status";
      help.className = largeHelp.className = "easy-upload-help";
      limits.className = "easy-upload-limits";
      email.href = "mailto:jiemg1989@gmail.com";
      largeText.append(email);
      largeHelp.append(largeTitle, largeText);
      help.append(helpTitle, guidance, helpText, limits, largeHelp);
      ((instructions.className = "easy-upload-four-instructions"),
        status.setAttribute("role", "status"),
        status.setAttribute("aria-live", "polite"),
        toolbar.append(add),
        section.append(
          style,
          title,
          instructions,
          rows,
          toolbar,
          summary,
          help,
          status,
        ),
        host.append(section));
      const scope =
        host.closest(
          "[data-easy-area-calculator], .easy-area-calculator, .product__info-container",
        ) || host;
      const pack =
        options.mode === 'pack' || isPrintSet ||
        Boolean(
          scope.querySelector(
            "[data-easy-flyer-size], [data-easy-booklet-size], [data-r=\"q\"]",
          ),
        );
      const perSet = () => {
        if (!pack) return 1;
        const value = Number(
          scope.querySelector("[data-easy-quantity], [data-r=\"q\"]")?.value,
        );
        return Number.isSafeInteger(value) && value > 0 ? value : null;
      };
      const quantityText = (sets) => {
        const count = perSet();
        if (!pack) return `本画面：${sets}个`;
        return count
          ? `本画面：${count * sets}个`
          : paperArtwork ? '请选择上方的每种图案印刷数量。' : `每套数量以上方选择为准，当前${sets}套。`;
      };
      const updateSummary = () => {
        const sets = serialize(state).totalPrintQuantity, count = perSet();
        summary.textContent = count
          ? paperArtwork ? `共${state.count}种图案，每种${count}个，合计${state.count * count}个` : `共${state.count}种画面，合计${sets * count}个`
          : paperArtwork ? `共${state.count}种图案，请选择每种图案印刷数量。` : `${state.count}种画面，共${sets}套；每套数量以上方选择为准。`;
        rows
          .querySelectorAll("[data-easy-set-explanation]")
          .forEach((node, i) => {
            node.textContent = quantityText(state.quantities[i]);
          });
      };
      const onConfigurationChange = (event) => {
        if (!section.contains(event.target)) updateSummary();
      };
      scope.addEventListener("change", onConfigurationChange);
      const controls = () => {
          (section.setAttribute("aria-busy", String(busy || pending)),
            section.querySelectorAll("button,input").forEach((node) => {
              node.disabled =
                busy ||
                node.dataset.easyFull === "1" ||
                node.dataset.easyConflict === "1";
            }),
            (add.disabled = busy || state.count === 5),
            (add.textContent =
              state.count === 5
                ? "已达5个画面上限"
                : "\uFF0B\u589E\u52A0\u4E0D\u540C\u753B\u9762"),
            options.onBusy?.(busy || pending));
        },
        commit = async (next) => {
          if (busy || destroyed)
            throw Error(
              "\u8BF7\u7B49\u5F85\u4E0A\u4F20\u6216\u4EF7\u683C\u6821\u9A8C\u5B8C\u6210\u3002",
            );
          const current = ++revision;
          delete status.dataset.error;
          if(paperArtwork)next=create({...next,quantities:Array(next.count).fill(1)});
          ((state = next),
            (pending = !0),
            render(),
            (status.textContent =
              "\u6B63\u5728\u8BA1\u7B97\u4EF7\u683C\u2026"));
          try {
            (await options.onChange(serialize(next), create(next)),
              !destroyed && current === revision && (status.textContent = ""));
          } catch (error) {
            if (current === revision) throw error;
          } finally {
            (current === revision && (pending = !1), destroyed || controls());
          }
        },
        run = (operation) => {
          operation().catch((error) => {
            status.dataset.error = "true";
            status.textContent =
              error.message ||
              "\u64CD\u4F5C\u5931\u8D25\uFF0C\u8BF7\u91CD\u8BD5\u3002";
          });
        },
        render = () => {
          (section
            .querySelectorAll(
              "[data-easy-replacement-input]:not([data-easy-replace-open])",
            )
            .forEach((node) => node.remove()),
            rows.replaceChildren(),
            updateSummary(),
            state.quantities.forEach((q, i) => {
              const group = make("fieldset"),
                legend = make("legend", `画面${i + 1}`),
                header = make("div"),
                heading = make("strong", `画面${i + 1}`),
                label = make("label", quantityLabel + "\uFF1A"),
                quantity = make("input");
              group.className = "easy-upload-artwork";
              group.dataset.easySides = String(state.sides);
              legend.className = "easy-upload-sr-only";
              header.className = "easy-upload-card-header";
              heading.setAttribute("aria-hidden", "true");
              header.append(heading);
              if(!paperArtwork)header.append(label);
              const setExplanation = make("p", quantityText(q));
              setExplanation.className = "easy-upload-set-explanation";
              setExplanation.setAttribute("data-easy-set-explanation", "");
              if (
                ((quantity.type = "number"),
                (quantity.min = "1"),
                (quantity.max = "1000000"),
                (quantity.step = "1"),
                (quantity.value = String(q)),
                quantity.setAttribute(
                  "aria-label",
                  `画面${i + 1}${quantityLabel}`,
                ),
                label.append(quantity),
                group.append(legend, header, setExplanation),
                quantity.setAttribute("data-easy-version-quantity", String(i)),
                state.count > 1)
              ) {
                const del = make("button", "移除画面");
                ((del.type = "button"),
                  del.setAttribute("data-easy-delete-version", String(i + 1)),
                  header.append(del),
                  del.addEventListener("click", () =>
                    run(async () => {
                      (["front", "back"].some(
                        (side) =>
                          assetsOf(state.files[`${i + 1}:${side}`]).length,
                      ) &&
                        !doc.defaultView.confirm(
                          `\u7248\u672C${i + 1}\u5DF2\u6709\u4E0A\u4F20\u6587\u4EF6\u3002\u786E\u5B9A\u4ECE\u672C\u6B21\u914D\u7F6E\u4E2D\u79FB\u9664\u6B64\u7248\u672C\u53CA\u5176\u6587\u4EF6\u5173\u8054\u5417\uFF1F`,
                        )) ||
                        (await commit(remove(state, i + 1)));
                    }),
                  ));
              }
              if (state.sides === 2) {
                const choice = make("label"),
                  checkbox = make("input"),
                  hint = make("p");
                if (
                  ((choice.className = "easy-same-front-back"),
                  (checkbox.type = "checkbox"),
                  (checkbox.checked = state.sameFrontBack[i]),
                  checkbox.setAttribute("data-easy-same-front-back", String(i)),
                  checkbox.setAttribute(
                    "aria-label",
                    `\u7248\u672C${i + 1}\u6B63\u53CD\u9762\u76F8\u540C`,
                  ),
                  (checkbox.dataset.easyConflict = assetsOf(
                    state.files[`${i + 1}:back`],
                  ).length
                    ? "1"
                    : "0"),
                  choice.append(
                    checkbox,
                    make("span", "正反面使用同一张图"),
                  ),
                  group.append(choice),
                  checkbox.checked)
                ) {
                  const property = make("input");
                  ((property.type = "hidden"),
                    (property.name = `properties[\u7248\u672C${i + 1}\u6B63\u53CD\u9762\u76F8\u540C]`),
                    (property.value =
                      "\u662F\uFF0C\u80CC\u9762\u4F7F\u7528\u6B63\u9762\u6587\u4EF6"),
                    group.append(property));
                }
                const front = state.files[`${i + 1}:front`],
                  back = state.files[`${i + 1}:back`];
                ((hint.className = "easy-same-front-back-hint"),
                  hint.setAttribute("role", "status"),
                  (hint.textContent = checkbox.checked
                    ? "\u5DF2\u9009\u62E9\u6B63\u53CD\u9762\u76F8\u540C\u3002\u66F4\u6362\u6B63\u9762\u6587\u4EF6\u540E\uFF0C\u80CC\u9762\u4F1A\u540C\u6B65\u66F4\u65B0\u3002"
                    : back?.url
                      ? "\u6B63\u53CD\u9762\u5DF2\u5206\u522B\u4E0A\u4F20\u3002"
                      : front?.url
                        ? "背面尚未上传，可先加入购物车或下单。图片数量和订单不匹配，将提醒本店人工核查；不会自动补图或删除文件。"
                        : "\u53CC\u9762\u5370\u5237\u8BF7\u5206\u522B\u4E0A\u4F20\u6B63\u9762\u3001\u80CC\u9762\uFF1B\u753B\u9762\u4E00\u6837\u65F6\u53EF\u52FE\u9009\u201C\u6B63\u53CD\u9762\u76F8\u540C\u201D\u3002"),
                  checkbox.dataset.easyConflict === "1" &&
                    (hint.textContent +=
                      " \u5DF2\u6709\u80CC\u9762\u6587\u4EF6\uFF0C\u4E0D\u4F1A\u81EA\u52A8\u8986\u76D6\u3002"),
                  (hint.hidden =
                    !front?.url && !back?.url && !checkbox.checked),
                  group.append(hint));
              }
              for (const item of plan(state).filter(
                (row) => row.version === i + 1,
              )) {
                if (item.sameAsFront) {
                  const note = make(
                    "div",
                    item.file?.url
                      ? "\u80CC\u9762\u4E0E\u6B63\u9762\u76F8\u540C\uFF0C\u4F7F\u7528\u4E0A\u65B9\u6B63\u9762\u6587\u4EF6\u3002"
                      : "\u80CC\u9762\u4E0E\u6B63\u9762\u76F8\u540C\uFF0C\u8BF7\u5148\u4E0A\u4F20\u6B63\u9762\u4E3B\u753B\u9762\u3002",
                  );
                  (note.setAttribute(
                    "data-easy-upload-slot",
                    String(item.slot),
                  ),
                    group.append(note));
                  continue;
                }
                const assets = assetsOf(item.file),
                  count = assets.length,
                  row = make("div"),
                  button = make(
                    "button",
                    options.filesOnly ? "上传文件（最多4个）" : item.side === "back" ? "背面上传" : "正面上传",
                  ),
                  input = make("input"),
                  fileStatus = make(
                    "span",
                    count ? `已上传${count}个，最多4个，还可上传${4 - count}个。` : "",
                  );
                fileStatus.className = "easy-upload-file-count";
                fileStatus.hidden = count === 0;
                (row.setAttribute("data-easy-upload-slot", String(item.slot)),
                  (button.type = "button"),
                  (button.dataset.easyFull = count === 4 ? "1" : "0"),
                  (input.type = "file"),
                  (input.hidden = !0),
                  (input.multiple = !0),
                  (input.accept = ".pdf,.ai,.eps,.jpg,.jpeg,.png,.zip,.rar"),
                  input.setAttribute(
                    "data-easy-shared-file",
                    String(item.slot),
                  ),
                  button.setAttribute(
                    "aria-label",
                    `\u4E0A\u4F20${item.label}\u6587\u4EF6\uFF08\u6700\u591A4\u4E2A\uFF09`,
                  ),
                  row.append(button, input, fileStatus),
                  group.append(row));
                const list = make("div");
                list.className = "easy-upload-four-files";
                row.append(list);
                for (const asset of assets) {
                  const card = make("div");
                  card.className = "easy-upload-four-file";
                  card.dataset.uploadLabel = item.label;
                  const caption = make(
                      "strong",
                      `${state.sides === 2 ? item.side === "front" ? "正面" : "背面" : "画面"}${asset.assetIndex}`,
                    ),
                    link = make("a", asset.name || "\u67E5\u770B\u6587\u4EF6");
                  if (window.EasyArtworkFit) {
                    link.href = asset.url;
                    link.target = '_blank';
                    link.rel = 'noopener';
                    window.EasyArtworkFit.mount(card, {asset, scope, previewUrl:thumbnailUrl(asset.url), label:`${item.label}${asset.assetIndex}`});
                  } else
                  if (
                    ((link.href = asset.url),
                    (link.target = "_blank"),
                    (link.rel = "noopener"),
                    /\.(png|jpe?g)(?:$|\?)/i.test(asset.name || asset.url))
                  ) {
                    const image = make("img");
                    ((image.src = thumbnailUrl(asset.url)),
                      (image.alt = caption.textContent),
                      (image.loading = "lazy"),
                      card.append(image));
                    image.onerror = () => { image.replaceWith(make("strong", `已上传 · ${fileType(asset.name || asset.url)}（无预览）`)); };
                  } else card.append(make("strong", `已上传 · ${fileType(asset.name || asset.url)}`));
                  const savedStatus = make("span", "已上传");
                  savedStatus.className = "easy-upload-saved-status";
                  savedStatus.setAttribute("role", "status");
                  const replace = make("button", "重新上传");
                  ((replace.type = "button"),
                    replace.setAttribute(
                      "aria-label",
                      `重新上传${item.label}${caption.textContent}`,
                    ));
                  (card.append(caption, link, savedStatus, replace),
                    list.append(card),
                    replace.addEventListener("click", () =>
                      run(async () => {
                        if (busy || pending)
                          throw Error(
                            "\u8BF7\u7B49\u5F85\u5F53\u524D\u64CD\u4F5C\u5B8C\u6210\u3002",
                          );
                        options.beforeOperation?.();
                        if (typeof options.clearFile != "function")
                          throw Error(
                            "\u6682\u4E0D\u80FD\u5B89\u5168\u66F4\u6362\u6587\u4EF6\u3002",
                          );
                        ((busy = !0),
                          controls(),
                          (state = clearAsset(
                            state,
                            item.version,
                            item.side,
                            asset.assetIndex,
                          )));
                        render();
                        try {
                          const results = await Promise.allSettled([
                            options.onChange(serialize(state), create(state)),
                            options.clearFile(asset, {
                              ...item,
                              slot: physicalSlot(item.slot, asset.assetIndex),
                              assetIndex: asset.assetIndex,
                            })
                          ]);
                          const failure = results.find(result => result.status === "rejected");
                          if (failure) throw failure.reason;
                          status.textContent = "原文件已删除，请点击上传文件。";
                        } finally {
                          ((busy = !1), render(), controls());
                        }
                      }),
                    ));
                }
                (button.addEventListener("click", () => {
                  if (pending) {
                    status.textContent =
                      "\u8BF7\u7B49\u5F85\u4EF7\u683C\u8BA1\u7B97\u5B8C\u6210\u540E\u4E0A\u4F20\u6587\u4EF6\u3002";
                    return;
                  }
                  input.click();
                }),
                  input.addEventListener("change", () =>
                    run(() =>
                      uploadSelection(Array.from(input.files || []), item),
                    ),
                  ));
              }
              rows.append(group);
            }),
            controls());
          if (options.filesOnly) {
            section.querySelectorAll(':scope > h3, :scope > .easy-upload-guidance, :scope > .easy-upload-toolbar, :scope > .easy-upload-total, .easy-upload-card-header, .easy-upload-set-explanation, legend').forEach(node => {
              node.hidden = true;
              node.style.setProperty('display', 'none', 'important');
            });
          }
        },
        uploadSelection = async (selected, item, replacementIndex) => {
          if (!selected.length) return;
          options.beforeOperation?.();
          if (busy || pending)
            throw Error(
              "\u8BF7\u7B49\u5F85\u5F53\u524D\u64CD\u4F5C\u5B8C\u6210\u3002",
            );
          const current = assetsOf(state.files[item.key]);
          if (selected.length + current.length > 4)
            throw Error(
              "\u6BCF\u4E2A\u4E0A\u4F20\u4F4D\u7F6E\u6700\u591A\u4E0A\u4F204\u4E2A\u6587\u4EF6\uFF0C\u8BF7\u51CF\u5C11\u9009\u62E9\u7684\u6587\u4EF6\u3002",
            );
          selected.forEach(validateFile);
          delete status.dataset.error;
          const available = [1, 2, 3, 4].filter(
            (index) => !current.some((asset) => asset.assetIndex === index),
          );
          ((busy = !0), controls());
          try {
            for (let index = 0; index < selected.length; index++) {
              const assetIndex = replacementIndex || available[index];
              status.textContent = `\u6B63\u5728\u4E0A\u4F20\u7B2C${index + 1}\u4E2A\u6587\u4EF6\uFF0C\u5171${selected.length}\u4E2A\u2026`;
              const uploaded = await options.upload(selected[index], {
                ...item,
                slot: physicalSlot(item.slot, assetIndex),
                uploadPosition: item.slot,
                assetIndex,
                onProgress: (percent, retrying) => {
                  status.textContent = `正在上传第${index + 1}个文件，共${selected.length}个：${percent}%${retrying ? '，网络不稳定，正在重试…' : '…'}`;
                },
                label: `${item.label}${assetIndex}`,
              });
              ((state = setFile(
                state,
                item.version,
                item.side,
                uploaded,
                assetIndex,
              )));
              // Show the saved original immediately. Quote validation is a
              // separate step and must never report a saved file as unuploaded.
              render();
              status.textContent = `第${index + 1}个文件已上传，正在保存订单关联…`;
              try {
                await options.onChange(serialize(state), create(state));
              } catch (error) {
                throw Error(`文件已上传，订单关联校验未完成，暂不能下单。请保留本页重试或联系客服。${index + 1 < selected.length ? `还有${selected.length - index - 1}个文件未上传。` : ""}`);
              }
            }
            status.textContent = "文件已上传并关联完成。";
          } finally {
            ((busy = !1), render(), controls());
          }
        };
      (section.addEventListener(
        "change",
        (event) => {
          const sameCheckbox = event.target.closest?.(
            "[data-easy-same-front-back]",
          );
          if (sameCheckbox) {
            const flags = [...state.sameFrontBack];
            ((flags[
              Number(sameCheckbox.getAttribute("data-easy-same-front-back"))
            ] = sameCheckbox.checked),
              run(() => commit(create({ ...state, sameFrontBack: flags }))));
            return;
          }
          const quantity = event.target.closest?.(
            "[data-easy-version-quantity]",
          );
          quantity &&
            run(async () => {
              const quantities = [...state.quantities];
              ((quantities[
                Number(quantity.getAttribute("data-easy-version-quantity"))
              ] = integer(quantity.value, 1, 1e6)),
                await commit(create({ ...state, quantities })));
            });
        },
        !0,
      ),
        add.addEventListener("click", () =>
          run(() => commit(resize(state, state.count + 1))),
        ));
      const api = {
        update: (changes = {}) =>
          commit(create({ ...state, count: changes.count ?? state.count, quantities: changes.quantities ?? state.quantities, sides: changes.sides ?? state.sides })),
        validate: () => {
          if (busy || pending)
            throw Error(
              "\u8BF7\u7B49\u5F85\u4E0A\u4F20\u6216\u62A5\u4EF7\u6821\u9A8C\u5B8C\u6210\u3002",
            );
          for (const input of section.querySelectorAll("input[type=number]"))
            if (!input.checkValidity() || Number(input.value) < 1)
              throw Error(
                "\u8BF7\u4E3A\u6BCF\u4E2A\u7248\u672C\u586B\u5199\u6709\u6548\u6570\u91CF\u3002",
              );
          return !0;
        },
        serialize: () => serialize(state),
        getState: () => create(state),
        destroy: () => {
          scope.removeEventListener("change", onConfigurationChange);
          ((destroyed = !0),
            section.remove(),
            delete host._easyMultiVersionUpload);
        },
      };
      return ((host._easyMultiVersionUpload = api), render(), api);
    };
  const endpoint = "https://shopify-banner-calculator.jiemg1989.workers.dev";
  const fileType = (name) => {
    const extension = String(name || '').split(/[?#]/)[0].match(/\.([a-z0-9]{1,8})$/i)?.[1]?.toUpperCase() || 'FILE';
    return extension === 'JPEG' ? 'JPG' : extension;
  };
  const thumbnailUrl = (url) => String(url || '').replace('/upload/file/', '/upload/thumbnail/');
  const withDeadline = (promise, milliseconds, fallback) => {
    let timer;
    return Promise.race([
      promise,
      new Promise(resolve => { timer = setTimeout(() => resolve(fallback), milliseconds); }),
    ]).finally(() => clearTimeout(timer));
  };
  // Preview only: never resize, recompress or replace the customer's original.
  const originalDimensions = new WeakMap();
  const createThumbnail = async (file) => {
    if (!/^image\/(png|jpeg)$/i.test(file.type) && !/\.(png|jpe?g)$/i.test(file.name || '')) return null;
    let image, url;
    try {
      image = new Image(); url = URL.createObjectURL(file);
      const loaded = await withDeadline(new Promise((resolve, reject) => {
        image.onload = () => resolve(true); image.onerror = reject; image.src = url;
      }), 5000, false);
      if (!loaded) return null;
      if (image.naturalWidth > 0 && image.naturalHeight > 0) originalDimensions.set(file,{width:image.naturalWidth,height:image.naturalHeight});
      const canvas = document.createElement('canvas'), ratio = Math.min(1, 200 / Math.max(image.naturalWidth, image.naturalHeight));
      canvas.width = Math.max(1, Math.round(image.naturalWidth * ratio));
      canvas.height = Math.max(1, Math.round(image.naturalHeight * ratio));
      const context = canvas.getContext('2d');
      context.fillStyle = '#fff'; context.fillRect(0, 0, canvas.width, canvas.height);
      context.drawImage(image, 0, 0, canvas.width, canvas.height);
      for (const quality of [0.45, 0.25, 0.1]) {
        const blob = await withDeadline(new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', quality)), 2000, null);
        if (blob && blob.size <= 12 * 1024) return blob;
      }
      return null;
    } catch (_) { return null; }
    finally { if (image) { image.onload = image.onerror = null; image.src = ''; } if (url) URL.revokeObjectURL(url); }
  };
  const transport = (productHandle) => Object.freeze({
    upload: async (file, row = {}) => {
      validateFile(file);
      const {onProgress, ...metadata} = row;
      let percent = 0;
      const request = async (action, body, session, attempts = 5) => {
        for (let attempt = 0; attempt < attempts; attempt++) {
          const controller = new AbortController(), timeout = setTimeout(() => controller.abort(), 120000);
          try {
            const response = await fetch(endpoint + '/upload/multipart/' + action, {
              method:'POST', headers:{'Content-Type':body instanceof Blob ? 'application/octet-stream' : 'application/json', ...(session ? {Authorization:'Bearer ' + session} : {})},
              body:body instanceof Blob ? body : JSON.stringify(body), signal:controller.signal
            });
            const result = await response.json();
            if (response.ok) return result;
            const error = Error(`上传未成功确认（${response.status}），请重试或联系客服。`);
            error.stop = response.status < 500 && response.status !== 408 && response.status !== 429;
            throw error;
          } catch (error) {
            if (error.stop || attempt + 1 === attempts) throw Error('上传结果未确认，请保留本页，不要下单；重试上传或联系客服确认。');
            onProgress?.(percent, true);
            await new Promise(resolve => setTimeout(resolve, [2000, 5000, 10000, 20000][attempt] || 20000));
          } finally { clearTimeout(timeout); }
        }
      };
      onProgress?.(0, false);
      const started = await request('start', {name:file.name, size:file.size, type:file.type, slot:Number(row.slot || 1), productHandle:String(productHandle || 'product')}, null, 1);
      if (!started.session || started.chunkSize !== 5 * 1024 * 1024) throw Error('上传通道初始化失败，请重试。');
      const thumbnailPromise = createThumbnail(file);
      const parts = [];
      for (let offset = 0; offset < file.size; offset += started.chunkSize) {
        const partNumber = parts.length + 1;
        const result = await request('part?part=' + partNumber, file.slice(offset, offset + started.chunkSize), started.session);
        if (result.partNumber !== partNumber || !result.etag) throw Error('文件分段未确认，请重试。');
        parts.push({partNumber, etag:result.etag});
        percent = Math.min(99, Math.floor(Math.min(file.size, offset + started.chunkSize) / file.size * 100));
        onProgress?.(percent, false);
      }
      const thumbnail = await thumbnailPromise;
      let preview;
      if (thumbnail) preview = btoa(String.fromCharCode(...new Uint8Array(await thumbnail.arrayBuffer())));
      let data;
      try { data = await request('complete', {parts, preview}, started.session); }
      catch (error) {
        // A lost completion response is not evidence that the saved file failed.
        const check = await request('status', {}, started.session).catch(() => null);
        if (!check?.complete || !check.file) throw error;
        data = check.file;
      }
      let url;
      try { url = new URL(data.url); } catch (_) {}
      if (url?.origin !== endpoint || !url.pathname.startsWith('/upload/file/') || !data.id || !data.token || data.size !== file.size) throw Error('文件保存结果尚未确认，请重试或联系客服。');
      onProgress?.(100, false);
      return { ...data, name: data.name || file.name, ...metadata, ...(originalDimensions.has(file) ? {artworkDimensions:originalDimensions.get(file)} : {}) };
    },
    remove: async (file) => {
      if (!file?.id || !file?.token) throw Error('旧文件缺少删除凭据，请联系客服处理。');
      const response = await fetch(endpoint + '/upload/remove', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: file.id, token: file.token }) });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.ok) throw Error(data.error || '旧文件清除失败，请重试。');
    },
    retain: async (files, orderId = 'shopify-cart') => {
      if (!files.length) return;
      const response = await fetch(endpoint + '/upload/extend', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ uploadedFiles: files, orderId }) });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw Error(data.error || '文件保存失败，请重试。');
    }
  });
  const properties = (metadata) => {
    const result = {
      '_Easy Artwork Versions': JSON.stringify(metadata.artworkVersions || []),
      '版本数量': String(metadata.versionCount || 1),
      '全部版本总套数': String(metadata.totalPrintQuantity || 1)
    };
    (metadata.uploadedFiles || []).forEach((file, index) => {
      const slot = file.slot || index + 1;
      result['Upload File ' + slot] = file.name + ': ' + file.url;
      result['_Upload File ' + slot + ' Token'] = file.id + '|' + file.token;
    });
    return result;
  };
  globalThis.EasyMultiVersionUpload = Object.freeze({
    MAX_VERSIONS: 5,
    MAX_FILES_PER_BUTTON: 4,
    physicalSlot,
    assetsOf,
    clearAsset,
    countFromAdditional,
    create,
    plan,
    resize,
    remove,
    setFile,
    serialize,
    validateFile,
    mount,
    transport,
    properties,
    mountFiles: (host, options) => mount(host, {
      filesOnly: true,
      files: { '1:front': {
        ...(options.files?.find(file => file.assetIndex === 1) || {}),
        attachments: (options.files || []).filter(file => file.assetIndex > 1),
      } },
      beforeOperation: options.beforeOperation,
      onBusy: options.onBusy,
      upload: options.upload,
      clearFile: options.clearFile,
      onChange: (metadata, state) => options.save(assetsOf(state.files['1:front'])),
    }),
  });
})();
//# sourceMappingURL=/cdn/shop/t/11/assets/easy-multi-version-upload.js.map?v=183428736502569492441791557104

